import { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquareQuote, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import { recordQuote } from '../api/procurementRfqApi';
import { formatMinor, majorToMinor } from '../constants';
import type { Rfq, RfqComparison } from '../types';

/**
 * Record (or correct) one supplier's quote against the RFQ's items.
 *
 * Rates are typed in MAJOR units (rupees) and converted to the paise the API
 * stores exactly once, on submit. The running line total shown while typing is
 * computed with the same arithmetic the server uses in quoteTotalMinor()
 * (qty × rate, less discount, plus tax) so the figure a buyer sees here matches
 * the total the comparison will rank — the authoritative total is still the one
 * the server returns, which is what replaces this screen's state on save.
 *
 * POST /:id/quotes is idempotent per supplier, so re-opening this for a
 * supplier who already quoted corrects that quote rather than adding a second
 * row, and the server preserves an existing selection flag while doing so.
 */

interface QuoteLineDraft {
  quotedRate: string;
  discountPct: string;
  taxPct: string;
  leadTimeDays: string;
  remarks: string;
}

function emptyLine(): QuoteLineDraft {
  return { quotedRate: '', discountPct: '0', taxPct: '0', leadTimeDays: '', remarks: '' };
}

function lineTotalMinor(item: { quantity: number }, line: QuoteLineDraft): number {
  const qty = Number(item.quantity) || 0;
  const rate = majorToMinor(line.quotedRate);
  const base = qty * rate;
  const discounted = base * (1 - (Number(line.discountPct) || 0) / 100);
  const taxed = discounted * (1 + (Number(line.taxPct) || 0) / 100);
  return Math.round(taxed);
}

export function RecordQuoteModal({
  open,
  rfq,
  initialSupplierId,
  onClose,
  onSaved,
}: {
  open: boolean;
  rfq: Rfq;
  initialSupplierId?: string | null;
  onClose: () => void;
  onSaved: (result: { rfq: Rfq; comparison: RfqComparison }) => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [status, setStatus] = useState<'received' | 'declined'>('received');
  const [terms, setTerms] = useState({ paymentTerms: '', deliveryTerms: '', warrantyTerms: '', remarks: '', declinedReason: '' });
  const [lines, setLines] = useState<QuoteLineDraft[]>([]);
  const [saving, setSaving] = useState(false);

  const items = useMemo(() => rfq.items || [], [rfq.items]);

  // Re-seed whenever the modal is opened for a different supplier. An existing
  // quote is pre-filled so a correction starts from what was quoted before,
  // rather than making the buyer retype every line.
  useEffect(() => {
    if (!open) return;
    const target = initialSupplierId || rfq.quotes?.[0]?.supplierId || '';
    setSupplierId(target);
    const existing = rfq.quotes?.find(q => q.supplierId === target);
    setStatus(existing?.status === 'declined' ? 'declined' : 'received');
    setTerms({
      paymentTerms: existing?.paymentTerms || '',
      deliveryTerms: existing?.deliveryTerms || '',
      warrantyTerms: existing?.warrantyTerms || '',
      remarks: existing?.remarks || '',
      declinedReason: existing?.declinedReason || '',
    });
    setLines(items.map((_, i) => {
      const quoted = existing?.lines?.find(l => l.itemIndex === i);
      if (!quoted) return emptyLine();
      return {
        quotedRate: quoted.quotedRateMinor ? String(quoted.quotedRateMinor / 100) : '',
        discountPct: String(quoted.discountPct ?? 0),
        taxPct: String(quoted.taxPct ?? 0),
        leadTimeDays: quoted.leadTimeDays === null || quoted.leadTimeDays === undefined ? '' : String(quoted.leadTimeDays),
        remarks: quoted.remarks || '',
      };
    }));
  }, [open, initialSupplierId, rfq, items]);

  const setLine = (i: number, key: keyof QuoteLineDraft, value: string) =>
    setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));

  const runningTotal = lines.reduce((sum, line, i) => sum + lineTotalMinor(items[i] || { quantity: 0 }, line), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierId) { toast.error('Choose which supplier this quote is from.'); return; }
    setSaving(true);
    try {
      const supplierName = rfq.quotes?.find(q => q.supplierId === supplierId)?.supplierName || '';
      const result = await recordQuote(rfq._id, {
        supplierId,
        supplierName,
        status,
        paymentTerms: terms.paymentTerms,
        deliveryTerms: terms.deliveryTerms,
        warrantyTerms: terms.warrantyTerms,
        remarks: terms.remarks,
        declinedReason: terms.declinedReason,
        // A declined quote carries no lines — the server rejects a 'received'
        // quote with none, and stores a declined one as a no-response slot.
        lines: status === 'declined' ? [] : lines.map((line, i) => ({
          itemIndex: i,
          productId: items[i]?.productId ?? null,
          description: items[i]?.description || '',
          quantity: Number(items[i]?.quantity) || 0,
          quotedRateMinor: majorToMinor(line.quotedRate),
          discountPct: Number(line.discountPct) || 0,
          taxPct: Number(line.taxPct) || 0,
          leadTimeDays: line.leadTimeDays === '' ? null : Number(line.leadTimeDays),
          remarks: line.remarks,
        })),
      });
      toast.success('Quote recorded.');
      onSaved(result);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not record this quote.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-sky-600">
          <div className="flex items-center gap-2 text-white">
            <MessageSquareQuote className="w-5 h-5" />
            <h2 className="text-lg font-bold">Record Supplier Quote</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier *</label>
              <select className={inputCls} value={supplierId} onChange={e => setSupplierId(e.target.value)}>
                <option value="">Select supplier...</option>
                {(rfq.quotes || []).map(q => (
                  <option key={q.supplierId} value={q.supplierId}>
                    {q.supplierName || q.supplierId.slice(-8)} ({q.status})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Response</label>
              <select className={inputCls} value={status} onChange={e => setStatus(e.target.value as 'received' | 'declined')}>
                <option value="received">Quoted</option>
                <option value="declined">Declined to quote</option>
              </select>
            </div>
          </div>

          {status === 'declined' ? (
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Reason for declining</label>
              <input className={inputCls} value={terms.declinedReason} onChange={e => setTerms(t => ({ ...t, declinedReason: e.target.value }))} placeholder="e.g. Material out of stock until Q3" />
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      {['Item', 'Qty', 'Rate (₹)', 'Disc %', 'Tax %', 'Lead (days)', 'Line Total'].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {items.map((item, i) => {
                      const line = lines[i] || emptyLine();
                      return (
                        <tr key={i} className="bg-white dark:bg-slate-900">
                          <td className="px-3 py-2">
                            <div className="text-slate-700 dark:text-slate-200">{item.description || 'Item'}</div>
                            {item.specification && <div className="text-xs text-slate-400">{item.specification}</div>}
                          </td>
                          <td className="px-3 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                            {item.quantity} {item.quantityUnit || ''}
                          </td>
                          <td className="px-3 py-2 w-28">
                            <input type="number" min="0" step="0.01" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none focus:border-indigo-400 py-1"
                              value={line.quotedRate} onChange={e => setLine(i, 'quotedRate', e.target.value)} placeholder="0.00" />
                          </td>
                          <td className="px-3 py-2 w-16">
                            <input type="number" min="0" max="100" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center py-1"
                              value={line.discountPct} onChange={e => setLine(i, 'discountPct', e.target.value)} />
                          </td>
                          <td className="px-3 py-2 w-16">
                            <input type="number" min="0" max="100" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center py-1"
                              value={line.taxPct} onChange={e => setLine(i, 'taxPct', e.target.value)} />
                          </td>
                          <td className="px-3 py-2 w-20">
                            <input type="number" min="0" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center py-1"
                              value={line.leadTimeDays} onChange={e => setLine(i, 'leadTimeDays', e.target.value)} placeholder="—" />
                          </td>
                          <td className="px-3 py-2 w-28 text-right font-medium text-slate-700 dark:text-slate-300">
                            {formatMinor(lineTotalMinor(item, line))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl px-6 py-3 text-right">
                  <div className="text-xs text-slate-500">Quote Total</div>
                  <div className="text-2xl font-black text-indigo-700">{formatMinor(runningTotal)}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Payment Terms</label><input className={inputCls} value={terms.paymentTerms} onChange={e => setTerms(t => ({ ...t, paymentTerms: e.target.value }))} placeholder="e.g. 30 days net" /></div>
                <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Delivery Terms</label><input className={inputCls} value={terms.deliveryTerms} onChange={e => setTerms(t => ({ ...t, deliveryTerms: e.target.value }))} placeholder="e.g. FOR site" /></div>
                <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Warranty</label><input className={inputCls} value={terms.warrantyTerms} onChange={e => setTerms(t => ({ ...t, warrantyTerms: e.target.value }))} placeholder="e.g. 12 months" /></div>
              </div>
              <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Remarks</label><textarea rows={2} className={inputCls} value={terms.remarks} onChange={e => setTerms(t => ({ ...t, remarks: e.target.value }))} /></div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Save Quote
          </button>
        </div>
      </form>
    </div>
  );
}

export default RecordQuoteModal;
